(() => {
  'use strict';

  if (window.__FTRFixedBundleDiscountDisplayFixV4Installed) return;
  window.__FTRFixedBundleDiscountDisplayFixV4Installed = true;

  const PREFIX = '[FTR Fixed Bundle Discount Display]';
  let refreshTimer = null;

  const text = (value) => value == null ? '' : String(value).trim();

  function positiveNumber(value) {
    if (value == null || value === '') return null;
    const number = Number(String(value).split('/').pop());
    return Number.isFinite(number) && number > 0 ? number : null;
  }

  function purchaseType(discount) {
    return text(
      discount?.purchaseType ||
      discount?.purchase_type ||
      discount?.appliesToPurchaseType ||
      discount?.purchaseOption
    ).toUpperCase();
  }

  function isSubscriptionDiscount(discount) {
    const type = purchaseType(discount);
    return type.includes('SUBSCRIPTION') || type.includes('SUBSCRIBE');
  }

  function discountId(discount) {
    return text(
      discount?.nanoId ||
      discount?.publicId ||
      discount?.discountId ||
      discount?.bundleDiscountId ||
      discount?.id
    ) || null;
  }

  function discountCode(discount) {
    return text(
      discount?.code ||
      discount?.discountCode ||
      discount?.couponCode ||
      discount?.promoCode
    ) || null;
  }

  // Keep this aligned with ftr-loop-bundle-discount-fix.js.
  function discountValue(discount) {
    const values = [
      discount?.value,
      discount?.discountValue,
      discount?.percentage,
      discount?.amount
    ];

    for (const value of values) {
      const number = Number(value);
      if (Number.isFinite(number)) return number;
    }

    return null;
  }

  // Keep this aligned with ftr-loop-bundle-discount-fix.js.
  function planComputedValue(discount, sellingPlanShopifyId) {
    const map = discount?.sellingPlanComputedDiscounts;
    if (!map || !sellingPlanShopifyId) return null;

    const direct = map[sellingPlanShopifyId] ?? map[String(sellingPlanShopifyId)];
    if (direct != null && Number.isFinite(Number(direct))) return Number(direct);

    const key = Object.keys(map).find(
      (entry) => String(entry).split('/').pop() === String(sellingPlanShopifyId)
    );
    if (!key) return null;

    const value = Number(map[key]);
    return Number.isFinite(value) ? value : null;
  }

  // This intentionally mirrors the cart-side discount collector. Do not add
  // independent widget-only inference here or the cart and PDP can disagree.
  function collectDiscounts(root, output = [], seen = new WeakSet(), depth = 0, source = 'unknown') {
    if (!root || depth > 9) return output;

    if (Array.isArray(root)) {
      root.forEach((entry) => collectDiscounts(entry, output, seen, depth + 1, source));
      return output;
    }

    if (typeof root !== 'object' || seen.has(root)) return output;
    seen.add(root);

    const id = discountId(root);
    const looksLikeDiscount = Boolean(id) && (
      root?.purchaseType != null ||
      root?.purchase_type != null ||
      root?.sellingPlanComputedDiscounts != null ||
      root?.oneTimeDiscount != null ||
      root?.discountValue != null ||
      root?.value != null ||
      root?.percentage != null ||
      root?.bundleDiscountId != null ||
      /discount/i.test(text(root?.__typename || root?.name))
    );

    if (looksLikeDiscount) output.push({ discount: root, source });

    Object.values(root).forEach((value) => {
      if (value && typeof value === 'object') {
        collectDiscounts(value, output, seen, depth + 1, source);
      }
    });

    return output;
  }

  function officialDetails(state) {
    return state?.bundleDetails?.data || state?.bundleDetails || {};
  }

  function hostedDetails(state) {
    return state?.hostedBundleDetails?.data || state?.hostedBundleDetails || {};
  }

  // Same selection/scoring rules as the working cart transaction resolver.
  function resolveSubscriptionDiscount(state, sellingPlanShopifyId) {
    const candidates = [];
    const selected = state?.selectedBundleDiscount;

    if (selected && discountId(selected)) {
      candidates.push({ discount: selected, source: 'state.selectedBundleDiscount' });
    }

    collectDiscounts(officialDetails(state), candidates, new WeakSet(), 0, 'official bundle');
    collectDiscounts(hostedDetails(state), candidates, new WeakSet(), 0, 'hosted bundle');

    const deduped = [];
    const seen = new Set();

    candidates.forEach((entry) => {
      const id = discountId(entry.discount);
      const key = `${id || 'none'}:${purchaseType(entry.discount)}:${entry.source}`;
      if (!id || seen.has(key)) return;
      seen.add(key);
      deduped.push(entry);
    });

    function score(entry) {
      const discount = entry.discount;
      let value = 0;

      if (!discountId(discount)) return -1000;
      if (isSubscriptionDiscount(discount)) value += 100;
      if (entry.source === 'state.selectedBundleDiscount') value += 80;
      if (entry.source === 'official bundle') value += 40;
      if (entry.source === 'hosted bundle') value += 30;
      if (planComputedValue(discount, sellingPlanShopifyId) != null) value += 60;
      if (discount?.sellingPlanComputedDiscounts) value += 20;
      if ((discountValue(discount) || 0) > 0) value += 10;
      if (discountCode(discount)) value += 25;

      return value;
    }

    deduped.sort((a, b) => score(b) - score(a));

    let chosen = deduped.find((entry) => isSubscriptionDiscount(entry.discount)) || null;
    if (!chosen && deduped.length === 1) chosen = deduped[0];
    if (!chosen && selected && discountId(selected)) {
      chosen = { discount: selected, source: 'state.selectedBundleDiscount fallback' };
    }

    console.log(PREFIX, 'Cart-aligned Loop discount resolution.', {
      sellingPlanShopifyId,
      candidates: deduped.map((entry) => ({
        source: entry.source,
        id: discountId(entry.discount),
        code: discountCode(entry.discount),
        purchaseType: purchaseType(entry.discount),
        value: discountValue(entry.discount),
        computed: planComputedValue(entry.discount, sellingPlanShopifyId),
        score: score(entry)
      })),
      chosen
    });

    return chosen;
  }

  function selectedPlanShopifyId(state, wrapper) {
    const selectedOption = wrapper?.querySelector(
      '.loop-purchase-option__frequency select option:checked'
    );

    const fromDom = positiveNumber(selectedOption?.dataset?.shopifySellingPlanId);
    if (fromDom) return fromDom;

    return positiveNumber(
      state?.selectedSellingPlanShopifyId ||
      state?.getSelectedSellingPlanId?.() ||
      state?.selectedSellingPlan?.adminGraphQlApiId ||
      state?.selectedSellingPlan?.shopifyId ||
      state?.sellingPlans?.[0]?.adminGraphQlApiId ||
      state?.sellingPlans?.[0]?.shopifyId
    );
  }

  function resolvePercentage(discount, sellingPlanShopifyId) {
    if (!discount) return null;

    // The selected Loop bundle discount itself is authoritative. In the working
    // cart flow this exact object supplies the discountId sent to Loop.
    const configuredValue = discountValue(discount);
    if (
      Number.isFinite(configuredValue) &&
      configuredValue > 0 &&
      configuredValue <= 100
    ) {
      return { percentage: configuredValue, source: 'selected Loop bundle discount value' };
    }

    // Only if the chosen discount has no configured value, use that same
    // discount object's selling-plan-specific computed value.
    const computed = planComputedValue(discount, sellingPlanShopifyId);
    if (
      Number.isFinite(computed) &&
      computed > 0 &&
      computed <= 100
    ) {
      return { percentage: computed, source: 'selected Loop discount sellingPlanComputedDiscounts' };
    }

    return null;
  }

  function currencyCode(state) {
    return state?.bundleDetails?.currencyCode ||
      state?.bundleDetails?.data?.currencyCode ||
      document.documentElement.dataset.currency ||
      'USD';
  }

  function formatMoney(cents, state) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: currencyCode(state)
      }).format(cents / 100);
    } catch (_) {
      return `$${(cents / 100).toFixed(2)}`;
    }
  }

  function clearDisplay(wrapper) {
    const state = window.FTRLoopBundle;
    const oneTimePrice = Number(wrapper.dataset.oneTimePrice || 0);
    const compare = wrapper.querySelector('[data-loop-subscription-compare-price]');
    const price = wrapper.querySelector('[data-loop-subscription-price]');
    const badge = wrapper.querySelector('[data-loop-save-badge]');

    if (price && oneTimePrice > 0) price.textContent = formatMoney(oneTimePrice, state);
    if (compare) {
      compare.hidden = true;
      compare.textContent = '';
    }
    if (badge) {
      badge.hidden = true;
      badge.textContent = '';
    }
  }

  function refreshWrapper(wrapper) {
    const state = window.FTRLoopBundle;
    if (!wrapper || !state?.bundleDetails) return false;

    const oneTimePrice = Number(wrapper.dataset.oneTimePrice || 0);
    if (!Number.isFinite(oneTimePrice) || oneTimePrice <= 0) return false;

    const sellingPlanShopifyId = selectedPlanShopifyId(state, wrapper);
    if (!sellingPlanShopifyId) return false;

    const resolved = resolveSubscriptionDiscount(state, sellingPlanShopifyId);
    const effective = resolvePercentage(resolved?.discount, sellingPlanShopifyId);

    if (!resolved || !effective) {
      clearDisplay(wrapper);
      return false;
    }

    const percentage = effective.percentage;
    const subscriptionPrice = Math.round(oneTimePrice * (1 - percentage / 100));
    const compare = wrapper.querySelector('[data-loop-subscription-compare-price]');
    const price = wrapper.querySelector('[data-loop-subscription-price]');
    const badge = wrapper.querySelector('[data-loop-save-badge]');

    if (compare) {
      compare.textContent = formatMoney(oneTimePrice, state);
      compare.hidden = false;
    }

    if (price) price.textContent = formatMoney(subscriptionPrice, state);

    if (badge) {
      badge.hidden = false;
      badge.textContent = `Save ${percentage}%`;
    }

    wrapper.dataset.ftrDisplayDiscount = String(percentage);

    console.log(PREFIX, 'Widget synchronized to the same Loop discount chosen by cart.', {
      sellingPlanShopifyId,
      percentage,
      percentageSource: effective.source,
      discountSource: resolved.source,
      discountId: discountId(resolved.discount),
      discountCode: discountCode(resolved.discount),
      discountValue: discountValue(resolved.discount),
      sellingPlanComputedValue: planComputedValue(resolved.discount, sellingPlanShopifyId),
      discount: resolved.discount,
      oneTimePrice,
      subscriptionPrice
    });

    return true;
  }

  function refreshAll() {
    document.querySelectorAll('[data-fixed-bundle-loop]').forEach(refreshWrapper);
  }

  function scheduleRefresh() {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(() => {
      refreshAll();
      requestAnimationFrame(refreshAll);
    }, 0);
  }

  function initialize(root = document) {
    const wrappers = [];
    if (root.matches?.('[data-fixed-bundle-loop]')) wrappers.push(root);
    root.querySelectorAll?.('[data-fixed-bundle-loop]').forEach((wrapper) => wrappers.push(wrapper));
    if (!wrappers.length) return;

    let attempts = 0;
    const timer = setInterval(() => {
      attempts += 1;
      const ready = wrappers.every((wrapper) => refreshWrapper(wrapper));
      if (ready || attempts >= 120) clearInterval(timer);
    }, 100);
  }

  document.addEventListener('change', (event) => {
    if (event.target.closest('[data-fixed-bundle-loop]')) scheduleRefresh();
  });

  document.addEventListener('click', (event) => {
    if (event.target.closest('[data-fixed-bundle-loop]')) scheduleRefresh();
  });

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  } else {
    initialize();
  }
})();
